import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapPin, Search, Users, Globe, Flag, Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ALL_COUNTRIES as countries } from '@/constants/countries';
import SearchableSelect from '@/components/SearchableSelect';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';

const FindExpats = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [nationality, setNationality] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [searchMode, setSearchMode] = useState('location'); // 'location', 'nationality', or 'name'
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [nationalities, setNationalities] = useState([]);
  const [countryOpen, setCountryOpen] = useState(false);
  const resultsRef = useRef(null);
  const hasAutoSearched = useRef(false);

  // Cache list state across route navigation so back from a profile lands
  // the user instantly on the same list at the results header.
  const listPersistence = useListStatePersistence({
    key: 'findExpats',
    state: { searchMode, city, country, nationality, nameSearch, results, hasSearched },
    enabled: hasSearched && results.length > 0,
    onRestore: (cached) => {
      if (cached.searchMode) setSearchMode(cached.searchMode);
      if (cached.city) setCity(cached.city);
      if (cached.country) setCountry(cached.country);
      if (cached.nationality) setNationality(cached.nationality);
      if (cached.nameSearch) setNameSearch(cached.nameSearch);
      if (Array.isArray(cached.results)) setResults(cached.results);
      if (cached.hasSearched) setHasSearched(true);
      // Block the URL-param auto-search effect from firing a redundant fetch.
      hasAutoSearched.current = true;
      // After cards are in the DOM, scroll to the "X Expats Found" header
      // instantly — no flash, no fight with the browser's auto-restore.
      requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });

  // Fetch nationalities for the dropdown
  useEffect(() => {
    fetchNationalities();
  }, []);

  // Handle URL params for pre-filled search (from "Find More" link or back button)
  useEffect(() => {
    const urlCity = searchParams.get('city');
    const urlCountry = searchParams.get('country');
    
    if (urlCity || urlCountry) {
      if (urlCity) setCity(urlCity);
      if (urlCountry && countries.includes(urlCountry)) setCountry(urlCountry);
      
      // Auto-search when we have URL params (covers both initial load and back button)
      if (!hasAutoSearched.current) {
        hasAutoSearched.current = true;
        // Small delay to let state update
        setTimeout(() => {
          handleSearchWithParams(urlCity, urlCountry);
        }, 100);
      }
    }
  }, [searchParams]);

  // Auto-detect location on page load (only if no URL params)
  useEffect(() => {
    const hasDetectedBefore = sessionStorage.getItem('location-detected');
    const urlCity = searchParams.get('city');
    const urlCountry = searchParams.get('country');
    
    if (!hasDetectedBefore && !city && !country && !urlCity && !urlCountry) {
      detectLocation(true);
    }
  }, []);

  const fetchNationalities = async () => {
    try {
      // Pass auth token so backend can exclude current user from counts
      const response = await axios.get(`${API}/nationalities`);
      setNationalities(response.data);
    } catch (error) {
      console.error('Failed to fetch nationalities');
    }
  };

  const detectLocation = async (isAutoDetect = false) => {
    if (!navigator.geolocation) {
      if (!isAutoDetect) {
        toast.error('Geolocation is not supported by your browser');
      }
      return;
    }

    setDetectingLocation(true);
    setLocationStatus('detecting');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Use reverse geocoding API to get city/country from coordinates
          // Added accept-language=en to always get English names
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=en`
          );
          const data = await response.json();
          
          if (data.address) {
            const detectedCity = data.address.city || data.address.town || data.address.village || data.address.municipality || '';
            const detectedCountry = data.address.country || '';
            
            if (detectedCity) setCity(detectedCity);
            if (detectedCountry && countries.includes(detectedCountry)) {
              setCountry(detectedCountry);
            }
            
            if (detectedCity || detectedCountry) {
              setLocationStatus('detected');
              sessionStorage.setItem('location-detected', 'true');
              toast.success(`📍 Location detected: ${detectedCity}${detectedCity && detectedCountry ? ', ' : ''}${detectedCountry}`, {
                description: isAutoDetect ? 'We found expats near you!' : undefined,
              });
            }
          }
        } catch (error) {
          console.error('Failed to reverse geocode:', error);
          setLocationStatus('error');
          if (!isAutoDetect) {
            toast.error('Failed to detect location. Please enter manually.');
          }
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setDetectingLocation(false);
        if (error.code === 1) { // Permission denied
          setLocationStatus('denied');
          if (!isAutoDetect) {
            toast.error('Location access denied. Please enter your location manually.');
          }
        } else {
          setLocationStatus('error');
          if (!isAutoDetect) {
            toast.error('Failed to detect location. Please try again.');
          }
        }
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    
    try {
      if (searchMode === 'nationality') {
        // Search by nationality
        if (!nationality) {
          toast.error('Please select a nationality');
          setSearching(false);
          return;
        }
        const response = await axios.get(`${API}/users/by-nationality/${encodeURIComponent(nationality)}`);
        setResults(response.data || []);
        
        if (response.data?.length === 0) {
          toast.info(`No ${nationality} expats found yet.`);
        }
      } else if (searchMode === 'name') {
        // Search by name
        if (!nameSearch.trim()) {
          toast.error('Please enter a name to search');
          setSearching(false);
          return;
        }
        
        const params = new URLSearchParams();
        params.append('search', nameSearch.trim());
        
        const response = await axios.get(`${API}/users/search?${params.toString()}`);
        setResults(response.data.users || response.data || []);
        
        if ((response.data.users || response.data)?.length === 0) {
          toast.info(`No expats found with name "${nameSearch}"`);
        }
      } else {
        // Search by location
        if (!city && !country) {
          toast.error('Please enter a city or select a country');
          setSearching(false);
          return;
        }
        
        const params = new URLSearchParams();
        if (city) params.append('city', city);
        if (country) params.append('country', country);
        
        // Update URL with search params (for back button support)
        setSearchParams(params, { replace: true });
        
        const response = await axios.get(`${API}/users/search?${params.toString()}`);
        setResults(response.data.users || response.data || []);
        
        if ((response.data.users || response.data)?.length === 0) {
          toast.info('No expats found in this location yet. Be the first to connect!');
        }
      }
      
      // Scroll to results after search
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Search with specific params (used for auto-search from URL)
  const handleSearchWithParams = async (searchCity, searchCountry) => {
    if (!searchCity && !searchCountry) return;
    
    setSearching(true);
    setHasSearched(true);
    
    try {
      const params = new URLSearchParams();
      if (searchCity) params.append('city', searchCity);
      if (searchCountry) params.append('country', searchCountry);
      
      const response = await axios.get(`${API}/users/search?${params.toString()}`);
      setResults(response.data.users || response.data || []);
      
      // Scroll to results after search
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
                <Globe className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                Find Expats Worldwide
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Connect with fellow expats in any city around the world. Enter a location to discover your community.
              </p>
            </div>

            {/* Search Form */}
            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border brutal-shadow mb-8">
              {/* Search Mode Tabs */}
              <Tabs value={searchMode} onValueChange={setSearchMode} className="mb-6">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="location" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    By Location
                  </TabsTrigger>
                  <TabsTrigger value="nationality" className="flex items-center gap-2">
                    <Flag className="w-4 h-4" />
                    By Nationality
                  </TabsTrigger>
                  <TabsTrigger value="name" className="flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    By Name
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="location">
                  {/* Location Detection Status Banner */}
                  {locationStatus === 'detecting' && (
                    <div className="mb-6 p-4 bg-accent/10 rounded-xl flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-accent font-medium">Detecting your location...</span>
                    </div>
                  )}
                  {locationStatus === 'detected' && city && (
                    <div className="mb-6 p-4 bg-green-50 rounded-xl flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 font-medium">
                        Location detected! Showing expats near {city}{country ? `, ${country}` : ''}
                      </span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-sm font-semibold">City</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                      placeholder={detectingLocation ? "Detecting..." : "Enter city name"}
                      className="h-12 pl-10 rounded-xl border-2"
                      data-testid="find-expats-city"
                      disabled={detectingLocation}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country" className="text-sm font-semibold">Country</Label>
                  <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={countryOpen}
                        className="w-full h-12 rounded-xl border-2 justify-between font-normal"
                        data-testid="find-expats-country"
                        disabled={detectingLocation}
                      >
                        {country || (detectingLocation ? "Detecting..." : "Select country")}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Type to search..." />
                        <CommandList>
                          <CommandEmpty>No country found.</CommandEmpty>
                          <CommandGroup>
                            {countries.map((c) => (
                              <CommandItem
                                key={c}
                                value={c}
                                onSelect={() => {
                                  setCountry(c === country ? '' : c);
                                  setCountryOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${country === c ? 'opacity-100' : 'opacity-0'}`} />
                                {c}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleSearch}
                  disabled={searching || detectingLocation || (!city && !country)}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-full font-bold text-lg brutal-shadow"
                  data-testid="find-expats-search"
                >
                  {searching ? (
                    'Searching...'
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Find Expats
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => detectLocation(false)}
                  disabled={detectingLocation}
                  variant="outline"
                  className="h-14 px-6 rounded-full font-bold border-2"
                  data-testid="detect-location"
                >
                  {detectingLocation ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      Detecting...
                    </>
                  ) : (
                    <>
                      <MapPin className="w-5 h-5 mr-2" />
                      Use My Location
                    </>
                  )}
                </Button>
              </div>
                </TabsContent>

                <TabsContent value="nationality">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Find Fellow Countrymen</Label>
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect with expats from your home country living abroad
                      </p>
                      <SearchableSelect
                        options={
                          nationalities.length > 0
                            ? nationalities.map((n) => ({
                                label: `${n.nationality} (${n.count} expat${n.count !== 1 ? 's' : ''})`,
                                value: n.nationality,
                              }))
                            : countries
                        }
                        value={nationality}
                        onValueChange={setNationality}
                        placeholder="Select nationality"
                        searchPlaceholder="Type to search..."
                        data-testid="find-expats-nationality"
                      />
                    </div>

                    <Button
                      onClick={handleSearch}
                      disabled={searching || !nationality}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-full font-bold text-lg brutal-shadow"
                    >
                      {searching ? (
                        'Searching...'
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Find {nationality || 'Expats'}
                        </>
                      )}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="name">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Search by Name</Label>
                      <p className="text-sm text-muted-foreground mb-4">
                        Looking for a specific person? Enter their name to find them.
                      </p>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          value={nameSearch}
                          onChange={(e) => setNameSearch(e.target.value)}
                          placeholder="Enter name (e.g., John, Maria)"
                          className="h-12 pl-10 rounded-xl border-2"
                          data-testid="find-expats-name"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && nameSearch.trim()) {
                              handleSearch();
                            }
                          }}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleSearch}
                      disabled={searching || !nameSearch.trim()}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-full font-bold text-lg brutal-shadow"
                    >
                      {searching ? (
                        'Searching...'
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Results */}
            {hasSearched && (
              <motion.div
                ref={resultsRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-3xl p-8 border-2 border-border">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-heading font-bold">
                      {results.length > 0 ? `${results.length} Expat${results.length !== 1 ? 's' : ''} Found` : 'No Results'}
                    </h2>
                    {searchMode === 'location' && (city || country) ? (
                      <span className="text-muted-foreground">
                        in {city?.trim()}{city?.trim() && country ? ', ' : ''}{country}
                      </span>
                    ) : searchMode === 'nationality' && nationality ? (
                      <span className="text-muted-foreground">
                        {nationality} expats worldwide
                      </span>
                    ) : null}
                  </div>

                  {results.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {results.map((expat, index) => (
                        <motion.div
                          key={expat.user_id || index}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          className="p-4 bg-background rounded-xl border-2 border-border hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => navigate(`/profile/${expat.user_id}`, { state: { from: 'expats', label: 'Expats' } })}
                          data-testid={`expat-card-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            {expat.profile_picture ? (
                              <img
                                src={expat.profile_picture}
                                alt={expat.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-border"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-primary-foreground">
                                {expat.name?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold">{expat.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {expat.residence_city}{expat.residence_city && expat.nationality ? ' • ' : ''}{expat.nationality}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg text-muted-foreground mb-4">
                        No expats found in this location yet.
                      </p>
                      <p className="text-muted-foreground">
                        Be the first to join this community!
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FindExpats;
